import React, { PropTypes } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import groupBy from 'lodash/groupBy';
import uniqBy from 'lodash/uniqBy';
import { NO_CATEGORY_ID_MAPPINGS } from 'constants';
import { apiDateFormatter, dateFormat } from 'lib/format';
import injectIntl, { intlShape } from 'common/injectIntl';
import { dateFormatSelector, upcomingScheduleDateFormatSelector, intlSelector } from 'common/selectors/appSelector';
import { preauthorizedResourcesSelector } from 'common/selectors/authenticationSelectors';
import darkThemeSelector from 'common/selectors/darkThemeSelector';
import ScheduleCategorySection from '@alias/ScheduleCategorySection';
import StatusFilter from './StatusFilter';
import isPacRimOrCarib from './isPacRimOrCarib';

const compareObjectsByName = (noCategoryMapping) => (
    (objA, objB) => {
        const aName = objA.name.trim().toLowerCase();
        const bName = objB.name.trim().toLowerCase();

        if (aName === noCategoryMapping) {
            return 1;
        }
        if (bName === noCategoryMapping) {
            return -1;
        }
        if (aName > bName) {
            return 1;
        } else if (aName < bName) {
            return -1;
        }
        return 0;
    }
);

const getTranslatedTab = (messages, formatMessageFn) => (
    (selectedTab = '', type = '') => {
        let key = `schedule.${selectedTab.toLowerCase()}`;
        if (type === 'selectedTabError') {
            key = `${key}.selectedTabError`;
        }

        const translatedSelectedTab = formatMessageFn(messages[key]) || '';

        if (translatedSelectedTab === '') {
            return selectedTab;
        }

        return translatedSelectedTab;
    }
);

const getTranslatedDate = (date, formatMessage, messages, lang) => {
    if (lang === 'en') {
        return date;
    }
    try {
        const arr = date.split(' ');
        let weekDay = arr[0].replace(',', '');
        weekDay = formatMessage(messages[`schedule.calendar.day.${weekDay}`]);
        const month = formatMessage(messages[`schedule.calendar.month.${arr[3]}`]);
        return `${weekDay} ${arr[1]} ${arr[2]} ${month}`;
    } catch (err) {
        console.error(err.messages);
        return date;
    }
};

const ScheduleCategoryContainer = ({
    listings,
    preauthorizedResources,
    selectedTab,
    startDateValue,
    userAuth,
    showSectionCaption,
    darkTheme,
    intl: { formatMessage },
    messages,
    intlDateFormat,
    upcomingDateFormat,
    bucketName,
    lang = 'en',
    watchRegion
}) => {
    if (listings.length === 0) {
        // Utility function used to tranlate specific tab by type
        const translateTab = getTranslatedTab(messages, formatMessage);
        const translatedSelectedTab = translateTab(selectedTab, 'selectedTabError');
        const otherTabs = [
            StatusFilter.Options.LIVE,
            StatusFilter.Options.UPCOMING,
            StatusFilter.Options.REPLAY
        ].filter((tab) => (tab !== selectedTab));

        return (
            <p>
                <FormattedMessage
                    {...messages['schedule.message.empty']}
                    values={{
                        br: <br />,
                        selectedTab: translatedSelectedTab,
                        otherTabs1: translateTab(otherTabs[0]).toLocaleLowerCase(),
                        otherTabs2: translateTab(otherTabs[1]).toLocaleLowerCase()
                    }}
                />
            </p>
        );
    }

    const renderTableCaption = (dateValue, formatOfDate, showCaption = false) => {
        /**
         * For isIntl, formatOfDate value is 'date15' as 'Weekday, number de month',
         * and formatOfDate default value is 'date10' as 'dddd, MMMM Do' for US.
         * We will check if watchRegion is PacRim or caribbean, and if it is true,
         * use 'date16' date formate as 'dddd, Do MMMM'
         */
        let date;
        if (isPacRimOrCarib(watchRegion)) {
            date = dateFormat.dateFormat(dateValue, 'date16');
        } else {
            const isNLEdition = lang && lang.toLowerCase() === 'nl';
            date = dateFormat.dateFormat(dateValue, formatOfDate);
            date = getTranslatedDate(date, formatMessage, messages, lang);
            if (isNLEdition) {
                date = date.replace('de', '').toLocaleLowerCase();
            }
        }
        // Handles the removal of `text-transform: capitalize` enforced by pageShell.css for Brazil
        const dateStyles = (formatOfDate === 'date15') ? { textTransform: 'none' } : {};

        return (showCaption === true) && <h2 style={dateStyles} className="table-caption">{date}</h2>;
    };

    // TODO: roll back to use `id` as key once category id is populated for E+ content from graphql
    const categories = uniqBy(listings.map((listing) => listing.categories[0]), 'name')
      .sort(compareObjectsByName(NO_CATEGORY_ID_MAPPINGS[lang]));
    const listingsGroupedByCategory = groupBy(listings, (listing) => listing.categories[0].name);
    const sections = categories.map((category) => {
        const categoryListings = listingsGroupedByCategory[category.name];
        return (
            <ScheduleCategorySection
                category={category}
                listings={categoryListings}
                userAuth={userAuth}
                key={`sched-category-${category.name}`}
                selectedTab={selectedTab}
                preauthorizedResources={preauthorizedResources}
                defaultLabel={formatMessage(messages['schedule.allLeagues'])}
                blackoutMessage={formatMessage(messages['schedule.blackout'])}
                dateFormat={intlDateFormat}
                showSectionCaption={showSectionCaption}
                darkTheme={darkTheme}
                bucketName={bucketName}
            />
        );
    });

    return (
        <div>
            {renderTableCaption(startDateValue, upcomingDateFormat, selectedTab === StatusFilter.Options.UPCOMING)}
            <div>{sections}</div>
        </div>
    );
};

ScheduleCategoryContainer.defaultProps = {
    selectedTab: StatusFilter.Options.LIVE,
    preauthorizedResources: [],
    startDateValue: apiDateFormatter(new Date())
};

ScheduleCategoryContainer.propTypes = {
    listings: PropTypes.array,
    userAuth: PropTypes.array,
    selectedTab: PropTypes.string,
    startDateValue: PropTypes.string,
    preauthorizedResources: PropTypes.array,
    showSectionCaption: PropTypes.bool,
    darkTheme: PropTypes.bool,
    intl: intlShape.isRequired,
    messages: PropTypes.object.isRequired,
    intlDateFormat: PropTypes.string,
    upcomingDateFormat: PropTypes.string,
    bucketName: PropTypes.string,
    lang: PropTypes.string,
    watchRegion: PropTypes.string
};

const mapStateToProps = (state) => ({
    preauthorizedResources: preauthorizedResourcesSelector(state),
    intlDateFormat: dateFormatSelector(state),
    upcomingDateFormat: upcomingScheduleDateFormatSelector(state),
    darkTheme: darkThemeSelector(state),
    lang: intlSelector(state).lang
});

export default connect(mapStateToProps)(injectIntl(ScheduleCategoryContainer));



// WEBPACK FOOTER //
// ./app/common/schedule/ScheduleCategoryContainer.js